import { type FunctionComponent, useEffect } from "react";
import "react-calendar/dist/Calendar.css";
import { CarLoadingAnimation } from "@App/Components/atoms/animations/CarLoadingAnimation";
import { bus } from "@App/domain/bus";
import { EventType } from "@App/domain/events";
import { useSlots } from "@App/hooks/api/useSlots";
import type { Slot } from "@App/types";
import { isSameDay, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

type TimeSlotPickerProps = {
  onChange: (event: Slot) => void;
  onChangeEarliestSlot: (event: Slot) => void;
  daysLoading?: boolean;
  day: Date | null;
  selectedSlot: Slot | null;
};

export const TimeSlotPicker: FunctionComponent<TimeSlotPickerProps> = ({
  onChange,
  onChangeEarliestSlot,
  daysLoading,
  day,
  selectedSlot,
}: TimeSlotPickerProps) => {
  const { t } = useTranslation();
  const { loading, nextSlot, slots, error } = useSlots(day);

  useEffect(() => {
    if (slots && slots.length > 0 && !selectedSlot) {
      onChange(slots[0]);
    }
    if (
      slots &&
      slots.length > 0 &&
      selectedSlot &&
      !isSameDay(parseISO(selectedSlot.starts_at), parseISO(slots[0].starts_at))
    ) {
      onChange(slots[0]);
    }
    onChangeEarliestSlot(nextSlot);
  }, [loading, slots, nextSlot, error]);

  if (error) {
    bus.publish({
      type: EventType.GENERIC_ERROR,
      message: "Failed to load slots for day",
      err: error,
    });
  }

  return (
    <div className={"relative"}>
      <div
        className={`
                relative
            md:max-h-[373.09px]
            flex flex-col
            md:text-sm
            bg-white
            border border-neutral-500 rounded
            p-3
            w-full`}
      >
        <p>Verfügbare Abgabezeit</p>
        <div
          className={`md:flex md:flex-col grid grid-cols-2 gap-2 mt-3 overflow-y-auto no-scrollbar`}
        >
          {error && <div>{t("error")}</div>}
          {(loading || daysLoading) &&
            Array(12)
              .fill("")
              .map((_, index) => (
                <span key={index} className={"w-full border rounded py-1 px-3"}>
                  &nbsp;
                </span>
              ))}
          {!loading &&
            !daysLoading &&
            !error &&
            selectedSlot &&
            slots &&
            slots.length > 0 &&
            slots.map((slot) => (
              <button
                type={"button"}
                className={`
                        text-center hover:text-white
                        rounded
                        py-1 px-3
                        hover:bg-primary-500
                        cursor-pointer
                        border hover:border-primary
                        ${
                          slot.starts_at === selectedSlot.starts_at
                            ? "bg-primary border-primary text-white"
                            : "text-primary border-neutral-500"
                        }`}
                onClick={() => onChange(slot)}
                key={slot.starts_at}
              >
                {slot.starts_at.slice(11, 16)}
              </button>
            ))}
        </div>
      </div>
      {(loading || daysLoading) && <TimeSlotPickerLoadingOverlay />}
    </div>
  );
};

function TimeSlotPickerLoadingOverlay() {
  return (
    <div className={"absolute inset-0 w-full bg-white/90 rounded"}>
      <div className={"flex items-center justify-center h-full w-full"}>
        <div className={"h-fit w-fit"}>
          <CarLoadingAnimation />
        </div>
      </div>
    </div>
  );
}
